// ABOUTME: REST hook for the variant pricing explain trace (POST /api/cpq/variant-pricing/explain).
// ABOUTME: Read-only debug call; returns the engine's per-rule trace joined to rule definitions and amounts.

import { useCallback, useState } from 'react';

export type Bucket = 'hoist' | 'trolley' | 'pendant' | 'chainContainer';

export interface ExplainRuleEntry {
    ruleIndex: number;
    type: 'tableEmit' | 'emit' | 'factor' | 'setTemp' | 'setFlag';
    fired: boolean;
    scope?: string;
    target?: Bucket;
    when?: string;
    key?: string;
    factor?: string;
    tableRef?: string;
    tableKeys?: Record<string, string>;
    temp?: string;
    value?: string;
    emittedKey?: string;
    appliedFactor?: number;
    writtenTemp?: string;
    bucket?: Bucket;
    amount?: number;
    runningTotal?: number;
    // The rule emitted a key with no price record and was not mandatory, so it was skipped
    // (SAP LO-VC parity): it fired but contributed nothing.
    unpriced?: boolean;
    // Why a false `when` guard skipped the rule: the operands the condition read and their values.
    // `fallback` is true when no operand path could be isolated and `operands` is the full scoped
    // input bag instead. Present only on non-fired rules.
    skipReason?: {
        operands: Array<{ path: string; value: unknown }>;
        fallback: boolean;
    };
}

export interface ExplainProcedureEntry {
    code: string;
    rules: ExplainRuleEntry[];
}

export interface ExplainNodeEntry {
    nodeId: string;
    modelId: string;
    procedures: ExplainProcedureEntry[];
}

export interface ExplainPriceResult {
    unitPrice: number;
    currency: string;
    byBucket: Record<Bucket, number>;
    breakdown: Array<{
        bucket: Bucket;
        key: string;
        factor: number;
        amount: number;
        source: { nodeId: string; procedure: string; ruleIndex: number };
    }>;
    nodes: ExplainNodeEntry[];
}

// Codes the variant pricing engine raises when it aborts mid-evaluation. Mirrors
// VariantPricingErrorCode on the server; each maps to a human-readable meaning in the drawer.
export type VariantPricingErrorCode =
    | 'MISSING_PRICE_KEY'
    | 'MODEL_HAS_NO_PRICES'
    | 'MISSING_TABLE_ROW'
    | 'MISSING_TABLE'
    | 'EXPRESSION_ERROR'
    | 'INVALID_RULE'
    | 'FACTOR_TARGET_NOT_FOUND'
    | 'MODEL_NOT_ASSIGNED'
    | 'MISSING_PROCEDURE'
    | 'CURRENCY_MISMATCH';

// The structured diagnostics the engine attaches to a VariantPricingError. Fields are code-specific
// and all optional; `source` and the flattened procedure/ruleIndex point at the offending rule.
export interface ExplainErrorDiagnostics {
    key?: string;
    bucket?: string;
    keyCurrency?: string;
    lineCurrency?: string;
    factor?: string;
    value?: unknown;
    nodeId?: string;
    procedure?: string;
    ruleIndex?: number;
    source?: { nodeId?: string; procedure?: string; ruleIndex?: number };
    [field: string]: unknown;
}

// A failed explain: the raw engine message plus, when the failure was a VariantPricingError, its
// code and diagnostics. Guard failures (auth, disabled tenant) carry only `message`. A pricing-phase
// failure also carries `nodes` — the full fired/not-fired rule trace that survived the throw.
export interface ExplainError {
    message: string;
    code?: VariantPricingErrorCode;
    diagnostics?: ExplainErrorDiagnostics;
    nodes?: ExplainNodeEntry[];
}

interface ApiResponse<T> {
    success: boolean;
    data?: T;
    error?: string;
}

// Error carrying the 422 payload's structured diagnostics so the drawer can render them, not just
// the flat message. Non-VariantPricingError failures leave code/diagnostics undefined.
class ExplainApiError extends Error {
    readonly code?: VariantPricingErrorCode;

    readonly diagnostics?: ExplainErrorDiagnostics;

    readonly nodes?: ExplainNodeEntry[];

    constructor(
        message: string,
        code?: VariantPricingErrorCode,
        diagnostics?: ExplainErrorDiagnostics,
        nodes?: ExplainNodeEntry[],
    ) {
        super(message);
        this.name = 'ExplainApiError';
        this.code = code;
        this.diagnostics = diagnostics;
        this.nodes = nodes;
    }
}

async function apiFetch<T>(url: string, options?: RequestInit): Promise<T> {
    const response = await fetch(url, {
        headers: { 'Content-Type': 'application/json' },
        ...options,
    });
    const json: ApiResponse<T> = await response.json();
    if (!json.success) {
        // On a VariantPricingError the server puts { code, diagnostics, nodes? } on `data`; keep it
        // so the drawer can explain which rule failed and still show the full rule trace.
        const errorData = json.data as
            | {
                  code?: VariantPricingErrorCode;
                  diagnostics?: ExplainErrorDiagnostics;
                  nodes?: ExplainNodeEntry[];
              }
            | undefined;
        throw new ExplainApiError(
            json.error ?? 'Unknown error',
            errorData?.code,
            errorData?.diagnostics,
            errorData?.nodes,
        );
    }
    return json.data as T;
}

/**
 * Hook for the read-only "explain price" endpoint. Requires the CPQ_PRICING_DEBUG role server-side;
 * without it the call answers 403 and the hook surfaces the error message.
 */
export function useExplainPrice() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<ExplainError | null>(null);
    const [result, setResult] = useState<ExplainPriceResult | null>(null);

    const explain = useCallback(async (configurationJson: string) => {
        setLoading(true);
        setError(null);
        setResult(null);
        try {
            const data = await apiFetch<ExplainPriceResult>('/api/cpq/variant-pricing/explain', {
                method: 'POST',
                body: JSON.stringify({ configurationJson }),
            });
            setResult(data);
            return data;
        } catch (err) {
            if (err instanceof ExplainApiError) {
                setError({
                    message: err.message,
                    code: err.code,
                    diagnostics: err.diagnostics,
                    nodes: err.nodes,
                });
            } else {
                setError({
                    message: err instanceof Error ? err.message : 'Failed to explain price',
                });
            }
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    return { loading, error, result, explain };
}
